import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button } from '../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';

import Layout from '../../Layout/Layout';
import { useGetAllCompanyDevices } from '../../../queries/devices';
import {
  useCreateVideoWall, useAssignDevices, useCalibrateWall, useUpdateVideoWall, useDeviceUsage,
  useGetVideoWall, useCreatePlaylist, useUpdatePlaylist, useStartWall, useGetWallMediaLibrary,
} from '../../../queries/videoWall';
import { useGetThemes } from '../../../queries/signageTemplates';
import { useCustomToast } from '../../Contexts/ToastContext';
import { resolveDeviceOnline } from '../../../utils/helpers';
import GridPreview from './components/GridPreview';
import PlaylistEditor from './components/PlaylistEditorDialog';
import PlaylistPreviewPlayer from './preview/PlaylistPreviewPlayer';

const WIZ_KEY = 'vw_wizard_v1'; // resume snapshot (cleared on finish / start-over)

/* Per-screen resolution presets (landscape values; swapped for portrait). */
const RESOLUTIONS = [
  { key: 'hd',     label: 'HD (1280×720)',    w: 1280, h: 720 },
  { key: 'fhd',    label: 'Full HD (1920×1080)', w: 1920, h: 1080 },
  { key: 'uhd',    label: '4K (3840×2160)',   w: 3840, h: 2160 },
];

/* Layout presets. custom is handled separately. */
const LAYOUTS = [
  { key: 'strip3p', i18n: 'video_wall.layout_3_portrait',  rows: 1, columns: 3, orientation: 'portrait',  emoji: '📱📱📱' },
  { key: 'grid2x2', i18n: 'video_wall.layout_2x2',         rows: 2, columns: 2, orientation: 'landscape', emoji: '🔳' },
  { key: 'strip3l', i18n: 'video_wall.layout_3_landscape', rows: 1, columns: 3, orientation: 'landscape', emoji: '🖥️🖥️🖥️' },
  { key: 'custom',  i18n: 'video_wall.layout_custom',      rows: 1, columns: 1, orientation: 'landscape', emoji: '⚙️' },
];

export default function VideoWallCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useCustomToast();

  const [step, setStep] = useState(1);
  const [layoutKey, setLayoutKey] = useState(null);
  const [cfg, setCfg] = useState({
    name: '',
    rows: 1,
    columns: 3,
    orientation: 'landscape',
    resKey: 'fhd',
  });
  const [assignments, setAssignments] = useState({}); // { position: device_id }
  const [wallId, setWallId] = useState(null);
  const [conflict, setConflict] = useState(null); // { msg } when a device is on another wall
  const [contentEditor, setContentEditor] = useState(false); // inline PlaylistEditor open
  const [started, setStarted] = useState(false); // celebration after Start

  const { data: usage = {} } = useDeviceUsage(); // device_id → { wall_id, wall_name }
  const { data: devicesData } = useGetAllCompanyDevices();
  const devices = useMemo(() => {
    const arr = devicesData?.data ?? devicesData ?? [];
    return Array.isArray(arr) ? arr : [];
  }, [devicesData]);

  const createWall = useCreateVideoWall();
  const assignDevices = useAssignDevices(wallId);
  const calibrate = useCalibrateWall(wallId);
  const updateWall = useUpdateVideoWall(wallId);
  const createPlaylist = useCreatePlaylist(wallId);
  const updatePlaylist = useUpdatePlaylist(wallId);
  const startWall = useStartWall(wallId);

  // Once the wall exists (created at the Devices step) read it back so the Content
  // and Start steps know whether content is present + every cell has a device.
  const { data: wall } = useGetVideoWall(wallId, !!wallId);
  const wallPlaylist = (wall?.playlists || []).find((p) => p.is_active) || (wall?.playlists || [])[0] || null;
  const effItems = wallPlaylist?.items || [];
  const hasContent = effItems.length > 0;
  const assignedScreens = (wall?.screens || []).filter((s) => s?.device).length;

  // Preview-step data (offline simulation of what Start will play). Same wiring as Show.
  const { data: themeData } = useGetThemes();
  const previewThemes = themeData?.themes ?? [];
  const previewZones = useMemo(() => themeData?.defaultZones ?? {}, [themeData]);
  const { data: previewMediaRaw } = useGetWallMediaLibrary(!!wallId);
  const previewMediaMap = useMemo(() => {
    const arr = previewMediaRaw?.data ?? previewMediaRaw ?? [];
    const m = {}; (Array.isArray(arr) ? arr : []).forEach((x) => { m[x.id] = x; }); return m;
  }, [previewMediaRaw]);
  const previewResolvePhoto = useMemo(() => (mid) => previewMediaMap[mid]?.path || previewMediaMap[mid]?.thumb || null, [previewMediaMap]);

  // ── Resume (P5): restore a half-finished wizard once on mount; persist as we go ──
  const restoredRef = React.useRef(false);
  React.useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;
    try {
      const s = JSON.parse(localStorage.getItem(WIZ_KEY) || 'null');
      if (s && s.wallId) {
        if (s.cfg) setCfg(s.cfg);
        if (s.assignments) setAssignments(s.assignments);
        if (s.layoutKey) setLayoutKey(s.layoutKey);
        setWallId(s.wallId);
        setStep(s.step || 1);
      }
    } catch { /* ignore malformed snapshot */ }
  }, []);
  React.useEffect(() => {
    // Only snapshot once the wall actually exists (after the Devices step) — a bare
    // step-1 with no wall should not trigger a resume prompt next visit.
    try {
      if (wallId) localStorage.setItem(WIZ_KEY, JSON.stringify({ step, cfg, assignments, layoutKey, wallId }));
    } catch { /* storage full / disabled — non-fatal */ }
  }, [step, cfg, assignments, layoutKey, wallId]);
  const clearWizard = () => { try { localStorage.removeItem(WIZ_KEY); } catch { /* noop */ } };
  const goToWall = () => { clearWizard(); navigate(`/apps/video-wall/${wallId}`); };
  const startOver = () => { clearWizard(); window.location.assign('/apps/video-wall/create'); };

  const res = RESOLUTIONS.find((r) => r.key === cfg.resKey) || RESOLUTIONS[1];
  const screenW = cfg.orientation === 'portrait' ? res.h : res.w;
  const screenH = cfg.orientation === 'portrait' ? res.w : res.h;
  const totalW = cfg.columns * screenW;
  const totalH = cfg.rows * screenH;
  const cellCount = cfg.rows * cfg.columns;

  const pickLayout = (l) => {
    setLayoutKey(l.key);
    if (l.key !== 'custom') {
      setCfg((c) => ({ ...c, rows: l.rows, columns: l.columns, orientation: l.orientation }));
    }
  };

  // Device dropdown option: online/offline dot + label.
  const deviceOptionTemplate = (opt) => (opt ? (
    <span className="d-inline-flex align-items-center gap-2" style={{ minWidth: 0 }}>
      <span style={{
        width: 8, height: 8, borderRadius: 999, flexShrink: 0,
        background: opt.online ? 'var(--pp-success)' : 'var(--pp-text-secondary)',
      }} title={opt.online ? t('video_wall.online') : t('video_wall.offline')} />
      <span className="text-truncate">{opt.label}</span>
    </span>
  ) : null);

  const screensPayload = () =>
    Object.entries(assignments)
      .filter(([, deviceId]) => !!deviceId)
      .map(([position, deviceId]) => ({ device_id: deviceId, grid_position: Number(position) }));

  const goToCalibration = async (transfer = false) => {
    const payload = {
      name: cfg.name?.trim() || t('video_wall.default_name'),
      rows: cfg.rows,
      columns: cfg.columns,
      orientation: cfg.orientation,
      screen_resolution_width: screenW,
      screen_resolution_height: screenH,
      screens: screensPayload(),
      transfer,
    };
    try {
      let id = wallId;
      if (!id) {
        const res = await createWall.mutateAsync(payload);
        const body = res?.data?.data ?? res?.data;
        if (body?.status === 409) { setConflict({ msg: body.msg }); return; } // device on another wall
        id = body?.wall?.id;
        setWallId(id);
      } else {
        const res = await assignDevices.mutateAsync({ screens: payload.screens, transfer });
        const body = res?.data?.data ?? res?.data;
        if (body?.status === 409) { setConflict({ msg: body.msg }); return; }
      }
      setConflict(null);
      if (id) setStep(4);
    } catch {
      /* toast handled in hook */
    }
  };

  // Step 5 (Name) → Step 6 (Content): persist the name, then move on.
  const saveNameAndContinue = async () => {
    try {
      if (wallId) await updateWall.mutateAsync({ name: cfg.name?.trim() || t('video_wall.default_name') });
      setStep(6);
    } catch { /* toast handled in hook */ }
  };

  // Inline content save (reuses the shared PlaylistEditor). Branch create/update so
  // re-opening the editor never spawns a second playlist.
  const savePlaylistContent = async (payload) => {
    try {
      if (wallPlaylist) await updatePlaylist.mutateAsync({ playlistId: wallPlaylist.id, ...payload });
      else await createPlaylist.mutateAsync(payload);
      setContentEditor(false);
    } catch { /* toast handled in hook */ }
  };

  const startNow = async () => {
    try {
      await startWall.mutateAsync({});
      setStarted(true);
    } catch { /* toast handled in hook */ }
  };

  /* ───────────── step guards ───────────── */
  const canNext = () => {
    if (step === 1) return !!layoutKey;
    if (step === 2) return cfg.rows >= 1 && cfg.columns >= 1;
    if (step === 3) return screensPayload().length > 0;
    return true;
  };

  const STEPS = [
    'video_wall.step_layout',
    'video_wall.step_orientation',
    'video_wall.step_devices',
    'video_wall.step_calibration',
    'video_wall.step_name',
    'video_wall.step_content',
    'video_wall.step_preview',
    'video_wall.step_start',
  ];

  return (
    <Layout>
      <div className="d-flex align-items-center gap-2 mb-3">
        <Button icon="pi pi-arrow-left" text rounded onClick={() => navigate('/apps/video-wall')} />
        <h3 className="mb-0" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.create_title')}</h3>
        {wallId && !started && (
          <Button label={t('video_wall.start_over')} icon="pi pi-refresh" text size="small" className="ms-auto"
            onClick={startOver} />
        )}
      </div>

      {/* Stepper */}
      <div className="d-flex flex-wrap gap-2 mb-4">
        {STEPS.map((key, i) => {
          const n = i + 1;
          const active = n === step;
          const done = n < step;
          return (
            <div key={key} className="d-flex align-items-center gap-2">
              <span
                style={{
                  width: 28, height: 28, borderRadius: '50%',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700, fontSize: 13,
                  background: active || done ? 'var(--pp-btn-honey)' : 'var(--pp-bg-tertiary)',
                  color: active || done ? '#fff' : 'var(--pp-text-secondary)',
                }}
              >
                {done ? <i className="pi pi-check" style={{ fontSize: 12 }} /> : n}
              </span>
              <small style={{ color: active ? 'var(--pp-text-primary)' : 'var(--pp-text-secondary)', fontWeight: active ? 600 : 400 }}>
                {t(key)}
              </small>
              {n < STEPS.length && <i className="pi pi-angle-right" style={{ color: 'var(--pp-text-secondary)' }} />}
            </div>
          );
        })}
      </div>

      <div
        className="p-4"
        style={{ background: 'var(--pp-bg-elevated)', border: '1px solid var(--pp-border)', borderRadius: 'var(--pp-radius-lg)' }}
      >
        {/* STEP 1 — Layout */}
        {step === 1 && (
          <>
            <h5 style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.step1_title')}</h5>
            <p style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.step1_subtitle')}</p>
            <div className="row g-3">
              {LAYOUTS.map((l) => {
                const selected = layoutKey === l.key;
                return (
                  <div key={l.key} className="col-6 col-md-3">
                    <button
                      type="button"
                      className="w-100 p-3 text-center"
                      style={{
                        background: selected ? 'color-mix(in srgb, var(--pp-btn-honey) 10%, transparent)' : 'var(--pp-bg-secondary)',
                        border: `2px solid ${selected ? 'var(--pp-btn-honey)' : 'var(--pp-border)'}`,
                        borderRadius: 'var(--pp-radius-md)', cursor: 'pointer',
                      }}
                      onClick={() => pickLayout(l)}
                    >
                      <div className="d-flex justify-content-center mb-2">
                        {l.key === 'custom'
                          ? <span style={{ fontSize: '2rem' }}>{l.emoji}</span>
                          : <GridPreview rows={l.rows} columns={l.columns} orientation={l.orientation} maxWidth={120} />}
                      </div>
                      <div style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>{t(l.i18n)}</div>
                    </button>
                  </div>
                );
              })}
            </div>

            {layoutKey === 'custom' && (
              <div className="row g-3 mt-2">
                <div className="col-6 col-md-3">
                  <label className="form-label" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.rows')}</label>
                  <InputNumber value={cfg.rows} min={1} max={10} showButtons
                    onValueChange={(e) => setCfg((c) => ({ ...c, rows: e.value || 1 }))} className="w-100" />
                </div>
                <div className="col-6 col-md-3">
                  <label className="form-label" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.columns')}</label>
                  <InputNumber value={cfg.columns} min={1} max={10} showButtons
                    onValueChange={(e) => setCfg((c) => ({ ...c, columns: e.value || 1 }))} className="w-100" />
                </div>
              </div>
            )}
          </>
        )}

        {/* STEP 2 — Orientation & Resolution */}
        {step === 2 && (
          <>
            <h5 style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.step2_title')}</h5>
            <div className="row g-4">
              <div className="col-12 col-md-6">
                <label className="form-label" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.orientation')}</label>
                <div className="d-flex gap-2">
                  {['landscape', 'portrait'].map((o) => (
                    <button key={o} type="button"
                      className="flex-grow-1 p-3"
                      style={{
                        background: cfg.orientation === o ? 'color-mix(in srgb, var(--pp-btn-honey) 10%, transparent)' : 'var(--pp-bg-secondary)',
                        border: `2px solid ${cfg.orientation === o ? 'var(--pp-btn-honey)' : 'var(--pp-border)'}`,
                        borderRadius: 'var(--pp-radius-md)', cursor: 'pointer', color: 'var(--pp-text-primary)', fontWeight: 600,
                      }}
                      onClick={() => setCfg((c) => ({ ...c, orientation: o }))}>
                      {o === 'landscape' ? '🖥️ ' : '📱 '}{t(`video_wall.orientation_${o}`)}
                    </button>
                  ))}
                </div>

                <label className="form-label mt-3" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.resolution_per_screen')}</label>
                <Dropdown
                  value={cfg.resKey}
                  options={RESOLUTIONS.map((r) => ({ label: r.label, value: r.key }))}
                  onChange={(e) => setCfg((c) => ({ ...c, resKey: e.value }))}
                  className="w-100"
                />
              </div>

              <div className="col-12 col-md-6 text-center">
                <label className="form-label d-block" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.grid_preview')}</label>
                <div className="d-flex justify-content-center">
                  <GridPreview rows={cfg.rows} columns={cfg.columns} orientation={cfg.orientation} maxWidth={260} />
                </div>
                <div className="mt-3 p-2" style={{ background: 'var(--pp-bg-secondary)', borderRadius: 'var(--pp-radius-md)' }}>
                  <small style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.total_resolution')}</small>
                  <div style={{ color: 'var(--pp-text-primary)', fontWeight: 700, fontSize: 18 }}>
                    {totalW.toLocaleString()} × {totalH.toLocaleString()} px
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* STEP 3 — Assign devices */}
        {step === 3 && (
          <>
            <h5 style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.step3_title')}</h5>
            <p style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.step3_subtitle')}</p>
            <div
              // Physical screen layout — pin LTR so RTL never mirrors which
              // device maps to which screen position.
              dir="ltr"
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${cfg.columns}, 1fr)`,
                gap: 12,
                maxWidth: cfg.columns * 220,
              }}
            >
              {Array.from({ length: cellCount }).map((_, idx) => {
                const position = idx + 1;
                const usedElsewhere = (id) =>
                  Object.entries(assignments).some(([p, d]) => d === id && Number(p) !== position);
                const options = devices
                  .filter((d) => !usedElsewhere(d.id))
                  .map((d) => ({
                    label: usage[d.id]
                      ? `${d.name || d.serial_name} · ${t('video_wall.on_wall', { name: usage[d.id].wall_name })}`
                      : `${d.name || d.serial_name}`,
                    value: d.id,
                    online: resolveDeviceOnline(d.connectivity, d.online),
                  }));
                return (
                  <div key={position} className="p-2" style={{ background: 'var(--pp-bg-secondary)', borderRadius: 'var(--pp-radius-md)', border: '1px solid var(--pp-border)' }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span style={{
                        width: 24, height: 24, borderRadius: '50%', background: 'var(--pp-primary)',
                        color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        fontWeight: 700, fontSize: 12,
                      }}>{position}</span>
                      <small style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.screen')} {position}</small>
                    </div>
                    <Dropdown
                      value={assignments[position] || null}
                      options={options}
                      onChange={(e) => setAssignments((a) => ({ ...a, [position]: e.value }))}
                      placeholder={t('video_wall.select_device')}
                      itemTemplate={deviceOptionTemplate}
                      valueTemplate={(opt) => (opt ? deviceOptionTemplate(opt) : t('video_wall.select_device'))}
                      filter showClear
                      className="w-100"
                    />
                  </div>
                );
              })}
            </div>
            {devices.length === 0 && (
              <p className="mt-3" style={{ color: 'var(--pp-warning)' }}>{t('video_wall.no_devices')}</p>
            )}
            {conflict && (
              <div className="mt-3 p-3 d-flex flex-wrap align-items-center gap-2"
                style={{ background: 'var(--pp-bg-secondary)', border: '1px solid var(--pp-warning)', borderRadius: 'var(--pp-radius-md)' }}>
                <i className="pi pi-exclamation-triangle" style={{ color: 'var(--pp-warning)' }} />
                <span className="small flex-grow-1" style={{ color: 'var(--pp-text-primary)' }}>{conflict.msg}</span>
                <Button label={t('video_wall.transfer_here')} icon="pi pi-arrow-right-arrow-left" size="small"
                  loading={createWall.isLoading || assignDevices.isLoading}
                  onClick={() => goToCalibration(true)} />
              </div>
            )}
          </>
        )}

        {/* STEP 4 — Calibration */}
        {step === 4 && (
          <div className="text-center">
            <h5 style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.step4_title')}</h5>
            <p style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.step4_subtitle')}</p>
            <div className="d-flex justify-content-center my-3">
              <GridPreview
                rows={cfg.rows} columns={cfg.columns} orientation={cfg.orientation} maxWidth={300}
                renderCell={(r, c, pos) => pos}
              />
            </div>
            <Button
              label={t('video_wall.run_calibration')}
              icon="pi pi-bolt"
              loading={calibrate.isLoading}
              onClick={() => calibrate.mutate()}
            />
            <div className="mt-3">
              <Button label={t('video_wall.rearrange')} text icon="pi pi-arrow-left" onClick={() => setStep(3)} />
            </div>
          </div>
        )}

        {/* STEP 5 — Name & Save */}
        {step === 5 && (
          <>
            <h5 style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.step5_title')}</h5>
            <label className="form-label" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.name')}</label>
            <InputText
              value={cfg.name}
              onChange={(e) => setCfg((c) => ({ ...c, name: e.target.value }))}
              placeholder={t('video_wall.name_placeholder')}
              className="w-100"
            />
          </>
        )}

        {/* STEP 6 — Content */}
        {step === 6 && (
          <>
            <h5 style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.step6_title')}</h5>
            <p style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.step6_subtitle')}</p>

            {hasContent && (
              <div className="mb-3 p-2 d-flex align-items-center gap-2"
                style={{ background: 'var(--pp-bg-secondary)', border: '1px solid var(--pp-success)', borderRadius: 'var(--pp-radius-md)' }}>
                <i className="pi pi-check-circle" style={{ color: 'var(--pp-success)' }} />
                <span className="small" style={{ color: 'var(--pp-text-primary)' }}>
                  {t('video_wall.content_added', { n: wallPlaylist?.items?.length || 0 })}
                </span>
                <Button label={t('video_wall.edit_content')} icon="pi pi-pencil" size="small" text className="ms-auto"
                  onClick={() => setContentEditor(true)} />
              </div>
            )}

            <div className="row g-3">
              {[
                { key: 'upload', icon: 'pi pi-images', onClick: () => setContentEditor(true) },
                { key: 'menu', icon: 'pi pi-list', onClick: () => navigate('/apps/video-wall/menus') },
                { key: 'template', icon: 'pi pi-palette', onClick: () => navigate('/apps/video-wall/templates') },
                { key: 'later', icon: 'pi pi-clock', onClick: () => setStep(7) },
              ].map((opt) => (
                <div key={opt.key} className="col-6 col-md-3">
                  <button type="button" className="w-100 h-100 p-3 text-center"
                    style={{
                      background: 'var(--pp-bg-secondary)', border: '2px solid var(--pp-border)',
                      borderRadius: 'var(--pp-radius-md)', cursor: 'pointer',
                    }}
                    onClick={opt.onClick}>
                    <i className={`${opt.icon}`} style={{ fontSize: '1.6rem', color: 'var(--pp-primary)' }} />
                    <div className="mt-2" style={{ color: 'var(--pp-text-primary)', fontWeight: 600 }}>{t(`video_wall.content_opt_${opt.key}`)}</div>
                    <small className="d-block mt-1" style={{ color: 'var(--pp-text-secondary)' }}>{t(`video_wall.content_opt_${opt.key}_sub`)}</small>
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {/* STEP 7 — Preview */}
        {step === 7 && (
          <>
            <h5 style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.preview_content')}</h5>
            <p style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.preview_content_hint')}</p>
            {hasContent ? (
              <div className="d-flex justify-content-center">
                <PlaylistPreviewPlayer items={effItems} maxWidth={520} lockGrid
                  initialGrid={{ rows: cfg.rows, cols: cfg.columns, orientation: cfg.orientation }}
                  mediaMap={previewMediaMap} themes={previewThemes} defaultZones={previewZones} resolvePhoto={previewResolvePhoto} />
              </div>
            ) : (
              <div className="text-center p-4" style={{ background: 'var(--pp-bg-secondary)', borderRadius: 'var(--pp-radius-md)' }}>
                <i className="pi pi-inbox" style={{ fontSize: '2rem', color: 'var(--pp-text-secondary)' }} />
                <p className="mt-2 mb-2" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.empty_content')}</p>
                <Button label={t('video_wall.content_opt_upload')} icon="pi pi-images" outlined size="small"
                  onClick={() => setStep(6)} />
              </div>
            )}
          </>
        )}

        {/* STEP 8 — Start */}
        {step === 8 && (
          <div className="text-center">
            {started ? (
              <>
                <div style={{ fontSize: '3rem' }}>🎉</div>
                <h5 className="mt-2" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.wall_is_live')}</h5>
                <p style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.wall_is_live_sub')}</p>
                <div className="d-flex justify-content-center my-3">
                  <GridPreview rows={cfg.rows} columns={cfg.columns} orientation={cfg.orientation} maxWidth={260}
                    renderCell={(r, c, pos) => pos} />
                </div>
                <Button label={t('video_wall.go_to_wall')} icon="pi pi-arrow-right" iconPos="right"
                  onClick={goToWall} />
              </>
            ) : (
              <>
                <h5 style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.step7_title')}</h5>
                <p style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.step7_subtitle')}</p>

                <div className="small d-inline-flex flex-column gap-1 my-3 text-start">
                  <span style={{ color: hasContent ? 'var(--pp-success)' : 'var(--pp-warning)' }}>
                    <i className={`pi ${hasContent ? 'pi-check-circle' : 'pi-exclamation-circle'} me-1`} />
                    {hasContent ? t('video_wall.ready_content_ok') : t('video_wall.ready_content_no')}
                  </span>
                  <span style={{ color: assignedScreens >= cellCount ? 'var(--pp-success)' : 'var(--pp-warning)' }}>
                    <i className={`pi ${assignedScreens >= cellCount ? 'pi-check-circle' : 'pi-exclamation-circle'} me-1`} />
                    {t('video_wall.ready_screens', { n: assignedScreens, total: cellCount })}
                  </span>
                </div>

                <div>
                  <Button label={t('video_wall.start')} icon="pi pi-play" severity="success" size="large"
                    disabled={!hasContent} loading={startWall.isLoading} onClick={startNow} />
                </div>
                {!hasContent && (
                  <div className="mt-2">
                    <Button label={t('video_wall.content_opt_upload')} icon="pi pi-images" text size="small"
                      onClick={() => setStep(6)} />
                  </div>
                )}
                <div className="mt-2">
                  <Button label={t('video_wall.go_to_wall')} text size="small"
                    onClick={goToWall} />
                </div>
              </>
            )}
          </div>
        )}

        {/* Footer nav */}
        {!started && (
        <div className="d-flex justify-content-between mt-4 pt-3" style={{ borderTop: '1px solid var(--pp-border)' }}>
          <Button
            label={t('video_wall.back')} icon="pi pi-arrow-left" text
            disabled={step === 1}
            onClick={() => setStep((s) => Math.max(1, s - 1))}
          />
          {step < 3 && (
            <Button label={t('video_wall.next')} icon="pi pi-arrow-right" iconPos="right"
              disabled={!canNext()} onClick={() => setStep((s) => s + 1)} />
          )}
          {step === 3 && (
            <Button label={t('video_wall.next')} icon="pi pi-arrow-right" iconPos="right"
              disabled={!canNext()} loading={createWall.isLoading || assignDevices.isLoading}
              onClick={() => goToCalibration()} />
          )}
          {step === 4 && (
            <Button label={t('video_wall.next')} icon="pi pi-arrow-right" iconPos="right"
              onClick={() => setStep(5)} />
          )}
          {step === 5 && (
            <Button label={t('video_wall.next')} icon="pi pi-arrow-right" iconPos="right"
              loading={updateWall.isLoading} onClick={saveNameAndContinue} />
          )}
          {step === 6 && (
            <Button label={t('video_wall.next')} icon="pi pi-arrow-right" iconPos="right"
              onClick={() => setStep(7)} />
          )}
          {step === 7 && (
            <Button label={t('video_wall.next')} icon="pi pi-arrow-right" iconPos="right"
              onClick={() => setStep(8)} />
          )}
        </div>
        )}
      </div>

      {wallId && (
        <PlaylistEditor
          visible={contentEditor}
          onHide={() => setContentEditor(false)}
          wall={wall || { rows: cfg.rows, columns: cfg.columns }}
          playlist={wallPlaylist}
          onSave={savePlaylistContent}
          saving={createPlaylist.isLoading || updatePlaylist.isLoading}
        />
      )}
    </Layout>
  );
}
